import { useCallback } from 'react';

/**
 * Custom hook for intelligent scrolling using scrollIntoView
 * Enhanced with MutationObserver and ResizeObserver for precise timing
 */

export function useSmartScroll() {
  /**
   * Scroll an element into view with smooth behavior
   * @param {HTMLElement} targetElement - The element to scroll to
   * @param {object} options - Scroll options
   * @param {string} options.block - Vertical alignment ('start', 'center', 'end', 'nearest')
   * @param {boolean} options.smooth - Whether to use smooth scrolling (default: true)
   */
  const scrollToElement = useCallback((targetElement, options = {}) => {
    if (!targetElement) {
      return;
    }
    const { block = 'start', smooth = true } = options;

    targetElement.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block,
      inline: 'nearest'
    });
  }, []);

  /**
   * Scroll to the bottom of the page/container with observer-based precision
   * @param {HTMLElement} containerElement - Container to find the last child in
   * @param {object} options - Scroll options
   */
  const scrollToBottom = useCallback((containerElement = null, options = {}) => {
    const { smooth = true } = options;

    const container = containerElement || document.body;
    const lastChild = container.lastElementChild;

    if (lastChild) {
      // Try scrollIntoView first
      lastChild.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end',
        inline: 'nearest'
      });

      // Single fallback check after reasonable delay
      setTimeout(() => {
        const targetScrollPosition = container.scrollHeight - container.clientHeight;
        const currentPosition = container.scrollTop;
        const isAtTargetPosition = Math.abs(currentPosition - targetScrollPosition) <= 5;

        if (!isAtTargetPosition) {
          container.scrollTo({
            top: targetScrollPosition,
            behavior: 'auto'
          });
        }
      }, 300);
    } else {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto'
      });
    }
  }, []);

  /**
   * Enhanced scroll to bottom that waits for content to be fully rendered
   * using MutationObserver and ResizeObserver
   */
  const scrollToBottomWhenReady = useCallback(
    (containerElement, options = {}) => {
      const { smooth = true, maxWaitTime = 1000, respectViewportTop = false } = options;

      if (!containerElement) {
        scrollToBottom(containerElement, { smooth });
        return;
      }

      let hasScrolled = false;
      let resizeObserver = null;
      let mutationObserver = null;

      const performScroll = () => {
        if (hasScrolled) return;
        hasScrolled = true;

        if (respectViewportTop) {
          // Find the last visible instruction group (not the sentinel)
          // Get all direct children instruction groups of the root
          const allInstructionGroups = Array.from(
            containerElement.querySelectorAll(':scope > .DynamicRenderer-instructionGroup')
          );
          const lastInstructionGroup = allInstructionGroups[allInstructionGroups.length - 1];

          // Instead of scrolling to the entire group, find a smaller target
          // Try to find the first text element inside the message
          const firstComponentInGroup = lastInstructionGroup?.querySelector(
            '.DynamicRenderer-componentWrapper'
          );

          // Look for the first actual text/content element inside the wrapper
          const firstTextElement =
            firstComponentInGroup?.querySelector('.MuiTypography-root') ||
            firstComponentInGroup?.querySelector('.Message-root');

          // Use the smallest element we can find: text > message > wrapper > group
          const scrollTarget = firstTextElement || firstComponentInGroup || lastInstructionGroup;

          if (scrollTarget) {
            // Get element and container positions
            const targetRect = scrollTarget.getBoundingClientRect();
            const containerRect = containerElement.getBoundingClientRect();

            // Calculate how much we need to scroll to bring target to container top
            const scrollOffset = targetRect.top - containerRect.top;

            // Get current scroll position
            const currentScroll = containerElement.scrollTop;

            // Calculate new scroll position
            const newScrollPosition = currentScroll + scrollOffset;

            // Clamp scroll position to valid range to prevent scrolling past top/bottom
            const maxScrollPosition = containerElement.scrollHeight - containerElement.clientHeight;
            const clampedScrollPosition = Math.max(
              0,
              Math.min(newScrollPosition, maxScrollPosition)
            );

            // Log if scroll position was clamped
            if (
              // process.env.NODE_ENV !== 'production' &&
              clampedScrollPosition !== newScrollPosition
            ) {
              console.warn('[scrollToBottomWhenReady] ⚠️ Scroll position clamped!', {
                original: newScrollPosition,
                clamped: clampedScrollPosition,
                maxScrollPosition,
                scrollHeight: containerElement.scrollHeight,
                clientHeight: containerElement.clientHeight,
                currentScroll,
                scrollOffset,
                targetTop: targetRect.top,
                containerTop: containerRect.top,
                reason: newScrollPosition < 0 ? 'below zero' : 'above max'
              });
            }

            // Manually scroll to the calculated position
            containerElement.scrollTo({
              top: clampedScrollPosition,
              behavior: smooth ? 'smooth' : 'auto'
            });
          }
        } else {
          scrollToBottom(containerElement, { smooth });
        }
        // Cleanup observers
        if (mutationObserver) mutationObserver.disconnect();
        if (resizeObserver) resizeObserver.disconnect();
      };

      // ResizeObserver to detect when container height changes (content added)
      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === containerElement) {
              // Small delay to ensure content is fully painted
              setTimeout(performScroll, 50);
              break;
            }
          }
        });
        resizeObserver.observe(containerElement);
      }

      // MutationObserver to detect when new DOM elements are added
      if (window.MutationObserver) {
        mutationObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
              // Check if any added node is an instruction group
              for (const node of mutation.addedNodes) {
                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  (node.classList?.contains('DynamicRenderer-instructionGroup') ||
                    node.querySelector?.('[class*="InstructionGroup"]'))
                ) {
                  // Small delay to ensure content is fully painted
                  setTimeout(performScroll, 50);
                  return;
                }
              }
            }
          }
        });

        mutationObserver.observe(containerElement, {
          childList: true,
          subtree: true
        });
      }

      // Fallback timeout in case observers don't fire
      setTimeout(() => {
        if (!hasScrolled) {
          performScroll();
        }
      }, maxWaitTime);

      // Immediate fallback if observers aren't supported
      if (!resizeObserver && !mutationObserver) {
        setTimeout(performScroll, 100);
      }
    },
    [scrollToBottom]
  );

  /**
   * Enhanced scroll to element that waits for target to be fully rendered
   */
  const scrollToElementWhenReady = useCallback(
    (targetElement, options = {}) => {
      const { maxWaitTime = 500 } = options;

      if (!targetElement) {
        return;
      }

      let hasScrolled = false;

      const performScroll = () => {
        if (hasScrolled) return;
        hasScrolled = true;

        scrollToElement(targetElement, options);
      };

      // Check if element is already fully rendered (has dimensions)
      const checkElementReady = () => {
        if (hasScrolled) return false;

        const rect = targetElement.getBoundingClientRect();
        const hasSize = rect.width > 0 && rect.height > 0;

        if (hasSize) {
          performScroll();
          return true;
        }

        return false;
      };

      // Immediate check
      if (checkElementReady()) return;

      // Use ResizeObserver if available to detect when element gets size
      if (window.ResizeObserver) {
        const resizeObserver = new ResizeObserver(() => {
          if (checkElementReady()) {
            resizeObserver.disconnect();
          }
        });
        resizeObserver.observe(targetElement);

        // Cleanup after max wait time
        setTimeout(() => {
          resizeObserver.disconnect();
        }, maxWaitTime);
      }

      // Fallback timeout
      setTimeout(() => {
        if (!hasScrolled) {
          performScroll();
        }
      }, maxWaitTime);
    },
    [scrollToElement]
  );

  return {
    scrollToElement,
    scrollToBottom,
    scrollToBottomWhenReady,
    scrollToElementWhenReady
  };
}
